import http from '@/utils/httpClient';
import { toaster } from '@/utils/toast';
import { Action, Thunk, action, thunk } from 'easy-peasy';

export interface profilePic {
  type: string;
  data: [];
}

export interface profileDescriptionAdvisor {
  advisorId: string;
  userId: string;
  firstName: string;
  lastName: string;
  emailId: string;
  type: string;
  company: string;
  role: string;
  linkedInUrl: string;
  firmAffiliation: string;
  phone: string;
  duties: string;
  crmValue: string;
  crmName: string;
  about: string;
  location: string;
  status: string;
  profilePic: profilePic;
  tosAcceptedAt: string | null;
}

export interface provider {
  userId: string;
  name: string;
  providerId: string;
  role: string;
  about: string;
  skills: string[];
  experience: string;
  profilePic: profilePic;
  status: string;
}

export interface providerSearchFilter {
  experience: string[];
  skills: string[];
}

export interface getProviderPayload {
  search: string;
  filter: providerSearchFilter;
}

export interface providersList {
  providers: provider[];
}

export interface workExperience {
  jobTitle: string;
  company: string;
  workType: string;
  jobType: string;
  description: string;
  startDate: string;
  endDate: string;
}

export interface education {
  degree: string;
  startDate: string;
  endDate: string;
  school: string;
}

export interface providerDescription {
  userId: string;
  name: string;
  emailId: string;
  type: string;
  status: string;
  providerId: string;
  linkedInUrl: string;
  phone: string;
  brokerValue: boolean;
  brokerName: string;
  experience: string;
  skills: string[];
  about: string;
  cv: string;
  role: string;
  profilePic: profilePic;
  location: string;
  workExperiences: workExperience[];
  education: education[];
}

export interface manageAdvisorSignature {
  sign: File;
}

export interface manageApplicant {
  job_id: string;
  provider_id: string;
}

export interface hireApplicant {
  job_id: string;
  provider_id: string;
  // sign: File
}

export interface rejectJobApplicants {
  job_id: string;
  provider_id: string;
}

export interface resumeDownloadPayload {
  fileUrl: string;
}

export interface IAdvisorModel {
  providers: provider[] | undefined;
  providerSearchFilter: getProviderPayload;
  providerDescription: providerDescription | undefined;
  profileDescription: profileDescriptionAdvisor | undefined;
  managerApplicant: manageApplicant;
  providerResume: any;

  setProviderList: Action<IAdvisorModel, provider[] | undefined>;
  setProviderSearchPayload: Action<IAdvisorModel, getProviderPayload>;
  setProviderDescription: Action<IAdvisorModel, providerDescription>;
  setProfileDescription: Action<IAdvisorModel, profileDescriptionAdvisor>;
  setManageApplicant: Action<IAdvisorModel, manageApplicant>;
  setProviderResume: Action<IAdvisorModel, any>;

  getProviderList: Thunk<IAdvisorModel, getProviderPayload>;
  getProviderDescription: Thunk<IAdvisorModel, string>;
  getProfileDescription: Thunk<IAdvisorModel>;
  manageJobApplicants: Thunk<IAdvisorModel, hireApplicant>;
  rejectJobApplicants: Thunk<IAdvisorModel, rejectJobApplicants>;
  editProfile: Thunk<IAdvisorModel, any>;
  downloadResume: Thunk<IAdvisorModel, any>;
}

const AdvisorFuntionsModel: IAdvisorModel = {
  providers: undefined,
  providerSearchFilter: { search: '', filter: { skills: [], experience: [] } },
  providerDescription: undefined,
  profileDescription: undefined,
  managerApplicant: { provider_id: '', job_id: '' },
  providerResume: '',

  /*------- ACTION type -------*/
  setProviderList: action((state, value) => {
    state.providers = value;
  }),

  setProviderSearchPayload: action((state, value) => {
    state.providerSearchFilter = value;
  }),

  setProviderDescription: action((state, value) => {
    state.providerDescription = value;
  }),

  setProfileDescription: action((state, value) => {
    state.profileDescription = value;
  }),

  setManageApplicant: action((state, value) => {
    state.managerApplicant = value;
  }),

  setProviderResume: action((state, value) => {
    state.providerResume = value;
  }),

  /*------- THUNK type -------*/

  getProviderList: thunk(async (action, payload) => {
    await http()
      .post('/provider/list', payload)
      .then((res) => {
        action.setProviderList(res.data.listOfProviders);
      })
      .catch(() => toaster('error', 'Error in fetching provider'));
  }),

  getProviderDescription: thunk(async (action, payload) => {
    const response = await http()
      .get(`/provider/profile/${payload}`)
      .then((res) => {
        action.setProviderDescription(res.data);
        return new Promise((resolve) => resolve(true));
      })
      .catch(() => {
        toaster('error', 'Error in fetching provider description');
        return new Promise((resolve) => resolve(false));
      });
    return response;
  }),

  getProfileDescription: thunk(async (action) => {
    await http()
      .get('/advisor/profile')
      .then((res) => {
        action.setProfileDescription(res.data.profile);
      })
      .catch(() => toaster('error', 'Error loading profile'));
  }),

  manageJobApplicants: thunk(async (_action, payload) => {
    return new Promise((resolve, reject) => {
      http()
        .put('/apply/hire', payload)
        .then(() => {
          toaster('success', 'Hired!');
          resolve(true);
        })
        .catch(() => {
          toaster('error', 'Error in hiring candidate');
          reject(false);
        });
    });
  }),

  rejectJobApplicants: thunk(async (_action, payload) => {
    await http()
      .get(`/apply/reject?job_post_id=${payload.job_id}&provider_id=${payload.provider_id}`)
      .then(() => {
        toaster('success', 'Rejected applicant');
      })
      .catch(() => {
        toaster('error', 'Error!!');
      });
  }),

  editProfile: thunk(async (_action, payload) => {
    const reponse = await http({ 'Content-Type': 'multipart/form-data' })
      .put('/advisor/profile', payload)
      .then(() => {
        toaster('success', 'Profile updated');
        return new Promise((resolve) => resolve('success'));
      })
      .catch(() => {
        toaster('error', 'Error in updating profile');
        return new Promise((resolve) => resolve('error'));
      });
  }),

  downloadResume: thunk(async (_action, payload) => {
    const response = await http()
      .post('/provider/file', payload)
      .then((res) => {
        const url = res.data?.message;
        _action.setProviderResume(url);
        if (url) {
          window.open(url);
        }
        return new Promise((resolve) => resolve('success'));
      })
      .catch(() => {
        toaster('error', 'Error fetching resume');
        return new Promise((resolve) => resolve('error'));
      });
    return response;
  }),
};
export default AdvisorFuntionsModel;
